import type { MatchState } from "../game/match.ts";
import { SLOT_COUNT } from "../heroes/types.ts";
import { abilIdx, HERO_VARS } from "../sim/components.ts";
import type { Sim } from "../sim/sim.ts";
import { encodeRecord, EntityType, LF, LI, LOCAL_F32, LOCAL_INT, type LocalState, STATUS_BITS, type WorldRecords } from "./snapshot.ts";

export const MATCH_NET_ID = 1;

export function statusBits(sim: Sim, eid: number): number {
  const S = sim.c.Status;
  let b = 0;
  if (S.stun[eid]! > 0) b |= STATUS_BITS.stun;
  if (S.root[eid]! > 0) b |= STATUS_BITS.root;
  if (S.slow[eid]! > 0) b |= STATUS_BITS.slow;
  if (S.sleep[eid]! > 0) b |= STATUS_BITS.sleep;
  if (S.invuln[eid]! > 0) b |= STATUS_BITS.invuln;
  if (S.knockdown[eid]! > 0) b |= STATUS_BITS.knockdown;
  if (S.boost[eid]! > 0) b |= STATUS_BITS.boost;
  if (S.phased[eid]! > 0) b |= STATUS_BITS.phased;
  if (S.held[eid]! > 0) b |= STATUS_BITS.pinned;
  if (S.antiHeal[eid]! > 0) b |= STATUS_BITS.antiHeal;
  if (S.healBoost[eid]! > 0) b |= STATUS_BITS.healBoost;
  if (S.resist[eid]! > 0) b |= STATUS_BITS.resist;
  if (S.linked[eid]! > 0) b |= STATUS_BITS.linked;
  if (S.stealth[eid]! > 0) b |= STATUS_BITS.stealth;
  if (S.haste[eid]! > 0) b |= STATUS_BITS.haste;
  if (S.unstoppable[eid]! > 0) b |= STATUS_BITS.unstoppable;
  if (S.marked[eid]! > 0) b |= STATUS_BITS.marked;
  if (S.bonded[eid]! > 0) b |= STATUS_BITS.bonded;
  if (S.frozen[eid]! > 0) b |= STATUS_BITS.frozen;
  if (S.held[eid]! > 0) b |= STATUS_BITS.held;
  return b;
}

/** Server: quantized replicated state of every entity this tick. */
export function captureWorld(sim: Sim, match?: { state: MatchState }): WorldRecords {
  const c = sim.c;
  const out: WorldRecords = new Map();
  for (const e of sim.playerEids()) {
    out.set(
      c.Net.id[e]!,
      encodeRecord(EntityType.Player, {
        hero: c.Player.hero[e]!,
        form: c.Player.form[e]!,
        baseHero: c.Player.baseHero[e]!,
        disguised: c.Player.disguiseTicks[e]! > 0 && c.Player.baseHero[e] !== c.Player.hero[e] ? 1 : 0,
        team: c.Player.team[e]!,
        alive: c.Player.alive[e]!,
        x: c.Pos.x[e]!,
        y: c.Pos.y[e]!,
        z: c.Pos.z[e]!,
        yaw: c.Look.yaw[e]!,
        pitch: c.Look.pitch[e]!,
        hp: Math.ceil(c.Health.hp[e]!),
        armor: Math.ceil(c.Health.armor[e]!),
        shield: Math.ceil(c.Health.shield[e]!),
        over: Math.ceil(c.Health.over[e]!),
        status: statusBits(sim, e),
        grounded: c.Player.grounded[e]!,
        crouch: c.Player.crouch[e]!,
        action: c.Weapon.action[e]!,
        shots: c.Weapon.shots[e]! & 0xff,
        moveKind: c.Move.ticks[e]! > 0 ? c.Move.kind[e]! : 0,
        ult: Math.floor(c.Ult.charge[e]! * 100),
        ultActive: c.Ult.active[e]! > 0 ? 1 : 0,
        respawn: Math.ceil(c.Player.respawn[e]! / 60),
        v0: Math.round(c.HeroVars[e * HERO_VARS]!),
        v1: Math.round(c.HeroVars[e * HERO_VARS + 1]!),
        v2: Math.round(c.HeroVars[e * HERO_VARS + 2]!),
        v3: Math.round(c.HeroVars[e * HERO_VARS + 3]!),
      }),
    );
  }
  for (const p of sim.projectileEids()) {
    const owner = c.Projectile.owner[p]!;
    out.set(
      c.Net.id[p]!,
      encodeRecord(EntityType.Projectile, {
        kind: c.Projectile.kind[p]!,
        owner: sim.players.has(owner) ? c.Net.id[owner]! : 0,
        seq: c.Projectile.seq[p]!,
        x: c.Pos.x[p]!,
        y: c.Pos.y[p]!,
        z: c.Pos.z[p]!,
        vx: c.Vel.x[p]!,
        vy: c.Vel.y[p]!,
        vz: c.Vel.z[p]!,
      }),
    );
  }
  for (const z of sim.zoneEids()) {
    out.set(
      c.Net.id[z]!,
      encodeRecord(EntityType.Zone, {
        kind: c.Zone.kind[z]!,
        team: c.Zone.team[z]!,
        x: c.Pos.x[z]!,
        y: c.Pos.y[z]!,
        z: c.Pos.z[z]!,
        radius: c.Zone.radius[z]!,
        ticks: c.Zone.ticks[z]!,
      }),
    );
  }
  for (const e of sim.constructEids()) {
    const owner = c.Construct.owner[e]!;
    out.set(
      c.Net.id[e]!,
      encodeRecord(EntityType.Construct, {
        kind: c.Construct.kind[e]!,
        owner: sim.players.has(owner) ? c.Net.id[owner]! : 0,
        team: c.Construct.team[e]!,
        x: c.Pos.x[e]!,
        y: c.Pos.y[e]!,
        z: c.Pos.z[e]!,
        yaw: c.Construct.yaw[e]!,
        hp: Math.ceil(c.Construct.hp[e]!),
        ticks: c.Construct.ticks[e]!,
        age: c.Construct.age[e]!,
        v0: c.Construct.v0[e]!,
        flags: c.Construct.flags[e]!,
      }),
    );
  }
  if (match) {
    const s = match.state;
    let packs = 0;
    sim.healthPacks.forEach((h, i) => {
      if (h.cooldown <= 0) packs |= 1 << i;
    });
    out.set(
      MATCH_NET_ID,
      encodeRecord(EntityType.Match, {
        phase: s.phase,
        phaseTicks: s.phaseTicks,
        round: s.round,
        scoreA: s.score[0],
        scoreB: s.score[1],
        progA: s.progress[0],
        progB: s.progress[1],
        owner: s.owner,
        capture: s.capture,
        capTeam: s.capTeam,
        contested: s.contested ? 1 : 0,
        overtime: s.overtime ? 1 : 0,
        onA: s.onPoint[0],
        onB: s.onPoint[1],
        packs,
        winner: s.winner,
        mode: s.mode,
        stage: s.stage,
        attacker: s.attacker,
        cart: s.cartMm,
        checkpoint: s.checkpoint,
        clock: s.clock,
        cartState: s.cartState,
        goalDone: s.goalDone ? 1 : 0,
        goalTicks: s.goalTicks,
      }),
    );
  }
  return out;
}

/** Server: exact predicted state for a client's own player. */
export function captureLocal(sim: Sim, eid: number, ackSeq: number): LocalState {
  const c = sim.c;
  const f = new Float32Array(LOCAL_F32.length);
  const n = new Int32Array(LOCAL_INT.length);
  f[LF.x] = c.Pos.x[eid]!;
  f[LF.y] = c.Pos.y[eid]!;
  f[LF.z] = c.Pos.z[eid]!;
  f[LF.vx] = c.Vel.x[eid]!;
  f[LF.vy] = c.Vel.y[eid]!;
  f[LF.vz] = c.Vel.z[eid]!;
  f[LF.ex] = c.Ext.x[eid]!;
  f[LF.ez] = c.Ext.z[eid]!;
  f[LF.slowPct] = c.Status.slowPct[eid]!;
  f[LF.hastePct] = c.Status.hastePct[eid]!;
  f[LF.ammo] = c.Weapon.ammo[eid]!;
  f[LF.stowed] = c.Weapon.stowed[eid]!;
  f[LF.ult] = c.Ult.charge[eid]!;
  f[LF.over] = c.Health.over[eid]!;
  for (let i = 0; i < HERO_VARS; i++) f[LF.hv0 + i] = c.HeroVars[eid * HERO_VARS + i]!;
  f[LF.mSpeed] = c.Move.speed[eid]!;
  f[LF.mx] = c.Move.x[eid]!;
  f[LF.my] = c.Move.y[eid]!;
  f[LF.mz] = c.Move.z[eid]!;
  for (let s = 0; s < SLOT_COUNT; s++) {
    f[LF.cd0 + s] = c.Abil.cd[abilIdx(eid, s)]!;
    n[LI.ch0 + s] = c.Abil.charges[abilIdx(eid, s)]!;
  }
  n[LI.hero] = c.Player.hero[eid]!;
  n[LI.form] = c.Player.form[eid]!;
  n[LI.baseHero] = c.Player.baseHero[eid]!;
  n[LI.disguiseTicks] = c.Player.disguiseTicks[eid]!;
  n[LI.grounded] = c.Player.grounded[eid]!;
  n[LI.crouch] = c.Player.crouch[eid]!;
  n[LI.buttons] = c.Player.buttons[eid]!;
  n[LI.aimSlot] = c.Player.aimSlot[eid]!;
  n[LI.aimTicks] = c.Player.aimTicks[eid]!;
  n[LI.aimMask] = c.Player.aimMask[eid]!;
  n[LI.stun] = c.Status.stun[eid]!;
  n[LI.root] = c.Status.root[eid]!;
  n[LI.slow] = c.Status.slow[eid]!;
  n[LI.sleep] = c.Status.sleep[eid]!;
  n[LI.invuln] = c.Status.invuln[eid]!;
  n[LI.knockdown] = c.Status.knockdown[eid]!;
  n[LI.boost] = c.Status.boost[eid]!;
  n[LI.phased] = c.Status.phased[eid]!;
  n[LI.pinned] = c.Status.held[eid]! > 0 ? 1 : 0;
  n[LI.antiHeal] = c.Status.antiHeal[eid]!;
  n[LI.healBoost] = c.Status.healBoost[eid]!;
  n[LI.resist] = c.Status.resist[eid]!;
  n[LI.linked] = c.Status.linked[eid]!;
  n[LI.stealth] = c.Status.stealth[eid]!;
  n[LI.haste] = c.Status.haste[eid]!;
  n[LI.unstoppable] = c.Status.unstoppable[eid]!;
  n[LI.marked] = c.Status.marked[eid]!;
  n[LI.markedBy] = c.Status.markedBy[eid]!;
  n[LI.bonded] = c.Status.bonded[eid]!;
  n[LI.bondedBy] = c.Status.bondedBy[eid]!;
  n[LI.frozen] = c.Status.frozen[eid]!;
  n[LI.held] = c.Status.held[eid]!;
  n[LI.heldBy] = c.Status.heldBy[eid]!;
  n[LI.reload] = c.Weapon.reload[eid]!;
  n[LI.fireCd] = c.Weapon.fireCd[eid]!;
  n[LI.action] = c.Weapon.action[eid]!;
  n[LI.actionTicks] = c.Weapon.actionTicks[eid]!;
  n[LI.shots] = c.Weapon.shots[eid]!;
  n[LI.weaponSlot] = c.Weapon.slot[eid]!;
  n[LI.ultActive] = c.Ult.active[eid]!;
  n[LI.overTicks] = c.Health.overTicks[eid]!;
  n[LI.mKind] = c.Move.kind[eid]!;
  n[LI.mTicks] = c.Move.ticks[eid]!;
  n[LI.mTarget] = c.Move.target[eid]!;
  n[LI.mBlocked] = c.Move.blocked[eid]!;
  return { netId: c.Net.id[eid]!, ackSeq, f32: f, ints: n };
}

/** Client: overwrite the prediction sim's local player with authoritative state. */
export function applyLocal(sim: Sim, eid: number, l: LocalState) {
  const c = sim.c;
  const f = l.f32;
  const n = l.ints;
  c.Pos.x[eid] = f[LF.x]!;
  c.Pos.y[eid] = f[LF.y]!;
  c.Pos.z[eid] = f[LF.z]!;
  c.Vel.x[eid] = f[LF.vx]!;
  c.Vel.y[eid] = f[LF.vy]!;
  c.Vel.z[eid] = f[LF.vz]!;
  c.Ext.x[eid] = f[LF.ex]!;
  c.Ext.z[eid] = f[LF.ez]!;
  c.Status.slowPct[eid] = f[LF.slowPct]!;
  c.Status.hastePct[eid] = f[LF.hastePct]!;
  c.Weapon.ammo[eid] = f[LF.ammo]!;
  c.Weapon.stowed[eid] = f[LF.stowed]!;
  c.Ult.charge[eid] = f[LF.ult]!;
  c.Health.over[eid] = f[LF.over]!;
  for (let i = 0; i < HERO_VARS; i++) c.HeroVars[eid * HERO_VARS + i] = f[LF.hv0 + i]!;
  c.Move.speed[eid] = f[LF.mSpeed]!;
  c.Move.x[eid] = f[LF.mx]!;
  c.Move.y[eid] = f[LF.my]!;
  c.Move.z[eid] = f[LF.mz]!;
  for (let s = 0; s < SLOT_COUNT; s++) {
    c.Abil.cd[abilIdx(eid, s)] = f[LF.cd0 + s]!;
    c.Abil.charges[abilIdx(eid, s)] = n[LI.ch0 + s]!;
  }
  const heroChanged = n[LI.hero]! !== c.Player.hero[eid] || n[LI.form]! !== c.Player.form[eid];
  c.Player.hero[eid] = n[LI.hero]!;
  c.Player.form[eid] = n[LI.form]!;
  c.Player.baseHero[eid] = n[LI.baseHero]!;
  c.Player.disguiseTicks[eid] = n[LI.disguiseTicks]!;
  const crouch = n[LI.crouch]!;
  if (heroChanged || crouch !== c.Player.crouch[eid]) {
    const def = sim.hero(eid);
    sim.physics.resizeCapsule(eid, def.radius, crouch ? def.height * 0.7 : def.height);
    c.Health.maxHp[eid] = def.health;
    c.Health.maxArmor[eid] = def.armor;
    c.Health.maxShield[eid] = def.shields;
    c.Player.crouch[eid] = crouch;
  }
  c.Player.grounded[eid] = n[LI.grounded]!;
  c.Player.buttons[eid] = n[LI.buttons]!;
  c.Player.aimSlot[eid] = n[LI.aimSlot]!;
  c.Player.aimTicks[eid] = n[LI.aimTicks]!;
  c.Player.aimMask[eid] = n[LI.aimMask]!;
  c.Status.stun[eid] = n[LI.stun]!;
  c.Status.root[eid] = n[LI.root]!;
  c.Status.slow[eid] = n[LI.slow]!;
  c.Status.sleep[eid] = n[LI.sleep]!;
  c.Status.invuln[eid] = n[LI.invuln]!;
  c.Status.knockdown[eid] = n[LI.knockdown]!;
  c.Status.boost[eid] = n[LI.boost]!;
  c.Status.phased[eid] = n[LI.phased]!;
  c.Status.held[eid] = n[LI.pinned]!;
  c.Status.antiHeal[eid] = n[LI.antiHeal]!;
  c.Status.healBoost[eid] = n[LI.healBoost]!;
  c.Status.resist[eid] = n[LI.resist]!;
  c.Status.linked[eid] = n[LI.linked]!;
  c.Status.stealth[eid] = n[LI.stealth]!;
  c.Status.haste[eid] = n[LI.haste]!;
  c.Status.unstoppable[eid] = n[LI.unstoppable]!;
  c.Status.marked[eid] = n[LI.marked]!;
  c.Status.markedBy[eid] = n[LI.markedBy]!;
  c.Status.bonded[eid] = n[LI.bonded]!;
  c.Status.bondedBy[eid] = n[LI.bondedBy]!;
  c.Status.frozen[eid] = n[LI.frozen]!;
  c.Status.held[eid] = n[LI.held]!;
  c.Status.heldBy[eid] = n[LI.heldBy]!;
  c.Weapon.reload[eid] = n[LI.reload]!;
  c.Weapon.fireCd[eid] = n[LI.fireCd]!;
  c.Weapon.action[eid] = n[LI.action]!;
  c.Weapon.actionTicks[eid] = n[LI.actionTicks]!;
  c.Weapon.shots[eid] = n[LI.shots]!;
  c.Weapon.slot[eid] = n[LI.weaponSlot]!;
  c.Ult.active[eid] = n[LI.ultActive]!;
  c.Health.overTicks[eid] = n[LI.overTicks]!;
  c.Move.kind[eid] = n[LI.mKind]!;
  c.Move.ticks[eid] = n[LI.mTicks]!;
  c.Move.target[eid] = n[LI.mTarget]!;
  c.Move.blocked[eid] = n[LI.mBlocked]!;
}
